import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Modal, Spin, Tag, message } from 'antd';
import { MailOutlined, StopOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { StatusMark } from '../../../components/StatusMark';
import { TargetInviteModal } from '../components/TargetInviteModal';
import { useTranslation } from '../../../i18n';
import { AddBlacklistModal } from '../creatorManager/AddBlacklistModal';
import { cancelInfluencerCollection, getInfluencerDetail } from '../../../store/request';
import type { InfluencerDetail } from '../../../types/influencer';

const regionLabelKey: Record<string, string> = {
  VN: 'talent.market_vn',
  TH: 'talent.market_th',
  ID: 'talent.market_id',
  MY: 'talent.market_my',
  PH: 'talent.market_ph',
  SG: 'talent.market_sg',
};

function dash(value?: string | number | null) {
  if (value == null || value === '') return '—';
  return String(value);
}

function formatCount(value?: number | null) {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US');
}

function formatGmv(amount?: number | null, currency?: string | null) {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const text = amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return currency ? `${currency} ${text}` : text;
}

function formatAgeRange(value?: string | null) {
  if (!value) return '—';
  const matched = /^AGE_RANGE_(\d+)_(\d+)$/.exec(value);
  if (matched) return `${matched[1]}-${matched[2]}`;
  return value;
}

function formatGenderShare(value?: number | null) {
  if (value == null || !Number.isFinite(value)) return '';
  return `${(value / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

function formatGender(value: string | null | undefined, translate: (key: string) => string) {
  if (value === 'MALE') return translate('talent.gender_male');
  if (value === 'FEMALE') return translate('talent.gender_female');
  return value || '';
}

function formatDemographics(raw: string | null | undefined, translate: (key: string) => string) {
  if (!raw) return '—';
  try {
    const parsed = JSON.parse(raw) as {
      age_ranges?: string[];
      major_gender?: { gender?: string; percentage?: number };
    };
    const ages = (parsed.age_ranges ?? [])
      .map((item) => formatAgeRange(item))
      .filter((item) => item !== '—');
    const gender = formatGender(parsed.major_gender?.gender, translate);
    const share = formatGenderShare(parsed.major_gender?.percentage);
    const genderText = [gender, share].filter(Boolean).join(' ');
    const text = [ages.join('、'), genderText].filter(Boolean).join(' · ');
    return text || '—';
  } catch {
    return raw;
  }
}

function parseUserTypeTags(raw?: string | null) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [raw];
  }
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 text-base font-semibold text-slate-800">{value}</div>
    </div>
  );
}

export const TalentDetail = observer(() => {
  const { t } = useTranslation();
  const { influencerId: influencerIdParam = '' } = useParams();
  const influencerId = decodeURIComponent(influencerIdParam).trim();
  const [detail, setDetail] = useState<InfluencerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [blacklistOpen, setBlacklistOpen] = useState(false);

  useEffect(() => {
    if (!influencerId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    getInfluencerDetail(influencerId)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          message.error(res.message || t('talent.detail_load_failed'));
          setDetail(null);
          return;
        }
        setDetail(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('talent.detail_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [influencerId, t]);

  const name = detail?.nickname || detail?.username || '—';
  const region = detail?.selectionRegion;
  const regionKey = region ? regionLabelKey[region] : undefined;
  const regionText = region ? (regionKey ? `${t(regionKey)} ${region}` : region) : '';
  const categories = detail?.categoryNames?.filter(Boolean) ?? [];
  const statusKey =
    detail?.status === 1
      ? 'replied'
      : detail?.status === 2
        ? 'cooperated'
        : detail?.status === 3
          ? 'blacklist'
          : 'pending';
  const statusLabel =
    statusKey === 'replied'
      ? t('talent.status_replied')
      : statusKey === 'cooperated'
        ? t('talent.status_cooperated')
        : statusKey === 'blacklist'
          ? t('talent.status_blacklist')
          : t('talent.status_pending');
  const genderLabel = formatGender(detail?.majorGender, t);
  const genderShare = formatGenderShare(detail?.majorGenderPercentage);
  const userTypes = parseUserTypeTags(detail?.userTypeTag);

  return (
    <div className="container">
      <header className="mb-5">
        <h1 className="mb-1 mt-0 text-[22px] font-semibold text-slate-800">{t('talent.detail_title')}</h1>
      </header>

      <Spin spinning={loading}>
        {!detail ? (
          <section className="wb-section">
            <p className="mb-0 text-sm text-slate-500">{loading ? '' : t('talent.no_record')}</p>
          </section>
        ) : (
          <>
            <section className="wb-section">
              <div className="flex min-w-0 gap-4">
                {detail.avatarUrl ? (
                  <img src={detail.avatarUrl} alt="" className="size-14 shrink-0 rounded-xl object-cover" />
                ) : (
                  <span className="inline-flex size-14 shrink-0 items-center justify-center rounded-xl bg-[#596ae8] text-xl font-semibold text-white">
                    {(name === '—' ? '?' : name).slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <h2 className="m-0 text-lg font-semibold text-slate-800">
                    {name}{' '}
                    {detail.username ? (
                      <span className="text-sm font-normal text-slate-400">@{detail.username}</span>
                    ) : null}
                  </h2>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {regionText ? (
                      <Tag className="m-0 border-0 bg-slate-100 text-slate-600">{regionText}</Tag>
                    ) : null}
                    {categories.map((item) => (
                      <Tag key={item} className="m-0 border-0 bg-slate-100 text-slate-600">
                        {item}
                      </Tag>
                    ))}
                    {categories.length === 0 && detail.categoryIds ? (
                      <Tag className="m-0 border-0 bg-slate-100 text-slate-600">{detail.categoryIds}</Tag>
                    ) : null}
                    {detail.status != null ? (
                      <StatusMark
                        tone={
                          statusKey === 'replied' || statusKey === 'cooperated'
                            ? 'success'
                            : statusKey === 'blacklist'
                              ? 'danger'
                              : 'pending'
                        }
                      >
                        {statusLabel}
                      </StatusMark>
                    ) : null}
                    {detail.isCollect ? (
                      <Tag className="m-0 border-0 bg-[#596ae8]/10 text-[#596ae8]">{t('talent.favorited')}</Tag>
                    ) : null}
                    {detail.isBlacklist && detail.status !== 3 ? (
                      <Tag className="m-0 border-0 bg-red-50 text-red-600">{t('talent.blacklist')}</Tag>
                    ) : null}
                    {detail.isOfficialRecommend ? (
                      <Tag className="m-0 border-0 bg-sky-50 text-sky-700">{t('talent.official_recommend')}</Tag>
                    ) : null}
                    {detail.isFastGrowing ? (
                      <Tag className="m-0 border-0 bg-orange-50 text-orange-600">{t('talent.fast_growing')}</Tag>
                    ) : null}
                    {detail.isRisingStar ? (
                      <Tag className="m-0 border-0 bg-amber-50 text-amber-600">{t('talent.rising_star')}</Tag>
                    ) : null}
                    {detail.hasCollaborated ? (
                      <Tag className="m-0 border-0 bg-emerald-50 text-emerald-600">{t('talent.collaborated')}</Tag>
                    ) : null}
                    {detail.hasInvited90d ? (
                      <Tag className="m-0 border-0 bg-cyan-50 text-cyan-700">{t('talent.invited_90d')}</Tag>
                    ) : null}
                    {detail.isCreatorBlocked ? (
                      <Tag className="m-0 border-0 bg-red-50 text-red-600">{t('talent.creator_blocked')}</Tag>
                    ) : null}
                    {detail.isEligibleFlatFee ? (
                      <Tag className="m-0 border-0 bg-violet-50 text-violet-700">{t('talent.flat_fee')}</Tag>
                    ) : null}
                    {userTypes.map((item) => {
                      const key = String(item);
                      const label =
                        key === '1'
                          ? t('talent.privacy_creator')
                          : key === '2'
                            ? t('talent.original_creator')
                            : key;
                      return (
                        <Tag key={key} className="m-0 border-0 bg-slate-100 text-slate-600">
                          {label}
                        </Tag>
                      );
                    })}
                  </div>
                  <p className="mb-0 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                    <span>
                      {t('talent.followers')}{' '}
                      <strong className="font-semibold text-slate-800">{formatCount(detail.followerCount)}</strong>
                    </span>
                    <span className="text-slate-300" aria-hidden>
                      |
                    </span>
                    <span>
                      {t('talent.pps_score')}{' '}
                      <strong className="font-semibold text-slate-800">{dash(detail.ppsScore)}</strong>
                    </span>
                    <span className="text-slate-300" aria-hidden>
                      |
                    </span>
                    <span>
                      {t('talent.creator_level')}{' '}
                      <strong className="font-semibold text-slate-800">{dash(detail.creatorLevel)}</strong>
                    </span>
                  </p>
                  {detail.creatorBindMcnName ? (
                    <p className="mb-0 mt-2 text-sm text-slate-500">
                      {t('talent.mcn')} {detail.creatorBindMcnName}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  disabled={!detail.isCollect}
                  onClick={() => {
                    const id = detail.influencerId?.trim() || influencerId;
                    if (!id) {
                      message.warning(t('talent.missing_open_id'));
                      return;
                    }
                    Modal.confirm({
                      title: t('creator.unfavorite'),
                      content: t('creator.unfavorite_confirm', { name }),
                      okText: t('common.confirm'),
                      cancelText: t('common.cancel'),
                      centered: true,
                      onOk: async () => {
                        const res = await cancelInfluencerCollection(id);
                        if (res.code !== 200) {
                          message.error(res.message || t('creator.unfavorite_failed'));
                          return Promise.reject(new Error(res.message || 'cancel failed'));
                        }
                        message.success(res.message || t('creator.unfavorite_success', { name }));
                        setDetail((prev) => (prev ? { ...prev, isCollect: false } : prev));
                      },
                    });
                  }}
                >
                  {t('creator.unfavorite')}
                </Button>
                <Button type="primary" className="wb-btn-primary" icon={<MailOutlined />}>
                  {t('talent.start_outreach')}
                </Button>
                <Button className="wb-btn-primary" icon={<ThunderboltOutlined />} onClick={() => setInviteOpen(true)}>
                  {t('talent.target_invite')}
                </Button>
                <Button icon={<MailOutlined />}>{t('talent.view_emails')}</Button>
                <Button
                  danger
                  icon={<StopOutlined />}
                  disabled={Boolean(detail.isBlacklist)}
                  onClick={() => {
                    const id = detail.influencerId?.trim() || influencerId;
                    if (!id) {
                      message.warning(t('creator.blacklist_missing_id'));
                      return;
                    }
                    setBlacklistOpen(true);
                  }}
                >
                  {detail.isBlacklist ? t('talent.blacklist') : t('talent.block')}
                </Button>
              </div>
            </section>

            <section className="wb-section mt-4">
              <div className="grid grid-cols-2 gap-4 min-[900px]:grid-cols-5">
                <Metric label={t('talent.gmv30')} value={formatGmv(detail.gmvAmount, detail.gmvCurrency)} />
                <Metric label={t('talent.live_gmv')} value={formatGmv(detail.liveGmvAmount, detail.gmvCurrency)} />
                <Metric label={t('talent.video_gmv')} value={formatGmv(detail.videoGmvAmount, detail.gmvCurrency)} />
                <Metric label={t('talent.avg_live_uv')} value={formatCount(detail.avgEcLiveUv)} />
                <Metric label={t('talent.avg_video_views')} value={formatCount(detail.avgEcVideoViewCount)} />
              </div>
            </section>

            <section className="wb-section mt-4">
              <h3 className="mb-4 mt-0 text-base font-semibold text-slate-800">{t('talent.audience')}</h3>
              <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-3">
                <Metric label={t('talent.age_range')} value={formatAgeRange(detail.topAgeRange)} />
                <Metric
                  label={t('talent.major_gender')}
                  value={genderLabel ? `${genderLabel}${genderShare ? ` ${genderShare}` : ''}` : '—'}
                />
                {/* <Metric label={t('talent.demographics')} value={formatDemographics(detail.topFollowerDemographics, t)} /> */}
              </div>
            </section>
          </>
        )}
      </Spin>

      <TargetInviteModal
        open={inviteOpen}
        talentIds={
          detail?.creatorOpenId?.trim()
            ? [detail.creatorOpenId.trim()]
            : []
        }
        talentName={detail?.nickname || detail?.username}
        onClose={() => setInviteOpen(false)}
      />
      <AddBlacklistModal
        open={blacklistOpen}
        influencerId={detail?.influencerId?.trim() || influencerId}
        talentName={name}
        onClose={() => setBlacklistOpen(false)}
        onAdded={() => setDetail((prev) => (prev ? { ...prev, isBlacklist: true } : prev))}
      />
    </div>
  );
});
